import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api, fmtNum, fmtDate } from "@/lib/api";
import PeriodFilter from "@/components/PeriodFilter";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, Filter, ChevronLeft, ChevronRight, X, Receipt } from "lucide-react";

const LIMIT = 25;

const fmtAmt = (v, cur) => {
  if (v === null || v === undefined) return "—";
  const n = Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return cur ? `${n} ${cur}` : n;
};

function StatusBadge({ status }) {
  const s = (status || "").toLowerCase();
  const cls = s === "completed"
    ? "bg-[#326F4B]/12 text-[#326F4B] hover:bg-[#326F4B]/12"
    : s === "failed" || s === "rejected" || s === "cancelled"
    ? "bg-destructive/10 text-destructive hover:bg-destructive/10"
    : "bg-[#D68C3C]/15 text-[#A7773F] hover:bg-[#D68C3C]/15";
  return <Badge variant="secondary" className={cls}>{status || "—"}</Badge>;
}

function SelectBox({ value, onChange, options, placeholder, testId }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className="h-10 w-full rounded-md border border-input bg-card px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring/40 focus:border-ring sm:w-auto"
    >
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={o} value={o}>{o}</option>
      ))}
    </select>
  );
}

export default function Transactions() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [provider, setProvider] = useState("");
  const [status, setStatus] = useState("");
  const [currency, setCurrency] = useState("");
  const [direction, setDirection] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortSel, setSortSel] = useState("created:desc");
  const [page, setPage] = useState(1);

  const [sortKey, order] = sortSel.split(":");
  const params = { q, provider, status, currency, direction, date_from: dateFrom, date_to: dateTo, page, limit: LIMIT, sort: sortKey, order };

  const { data: opts } = useQuery({ queryKey: ["tx-filters"], queryFn: () => api.get("/transactions/filters").then((r) => r.data) });
  const { data, isLoading } = useQuery({
    queryKey: ["transactions", params],
    queryFn: () => api.get("/transactions", { params }).then((r) => r.data),
  });

  const rows = data?.rows || [];
  const total = data?.total || 0;
  const totalPages = Math.max(1, Math.ceil(total / LIMIT));
  const onFilter = (setter) => (v) => { setter(v); setPage(1); };
  const hasFilters = q || provider || status || currency || direction || dateFrom || dateTo;
  const reset = () => { setQ(""); setProvider(""); setStatus(""); setCurrency(""); setDirection(""); setDateFrom(""); setDateTo(""); setSortSel("created:desc"); setPage(1); };

  const openCustomer = (t) => { if (t.customer_id) navigate(`/customers/${t.customer_id}`); };

  return (
    <div className="space-y-6" data-testid="transactions-page">
      <div>
        <h1 className="font-heading text-3xl font-semibold tracking-tight">Transactions</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Search and filter every imported transaction — <span className="font-medium text-foreground">{fmtNum(total)}</span> records.
        </p>
      </div>

      <div className="rounded-md border border-border bg-card p-4">
        <div className="mb-3 flex items-center gap-2 text-sm font-medium text-muted-foreground">
          <Filter className="h-4 w-4" /> Filters
          {hasFilters && (
            <button onClick={reset} className="ml-auto flex items-center gap-1 text-xs text-destructive hover:underline" data-testid="tx-reset-filters">
              <X className="h-3 w-3" /> Reset
            </button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative w-full sm:min-w-[220px] sm:flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => onFilter(setQ)(e.target.value)} placeholder="Transaction ID or account ID..." className="pl-9" data-testid="tx-search" />
          </div>
          <SelectBox value={provider} onChange={onFilter(setProvider)} options={opts?.providers || []} placeholder="All Providers" testId="tx-filter-provider" />
          <SelectBox value={status} onChange={onFilter(setStatus)} options={opts?.statuses || []} placeholder="All Statuses" testId="tx-filter-status" />
          <SelectBox value={currency} onChange={onFilter(setCurrency)} options={opts?.currencies || []} placeholder="All Currencies" testId="tx-filter-currency" />
          <SelectBox value={direction} onChange={onFilter(setDirection)} options={opts?.directions || []} placeholder="All Directions" testId="tx-filter-direction" />
          <select
            value={sortSel}
            onChange={(e) => { setSortSel(e.target.value); setPage(1); }}
            data-testid="tx-sort-select"
            className="h-10 w-full rounded-md border border-input bg-card px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring/40 focus:border-ring sm:w-auto"
          >
            <option value="created:desc">Newest first</option>
            <option value="created:asc">Oldest first</option>
            <option value="amount:desc">Amount: high → low</option>
            <option value="amount:asc">Amount: low → high</option>
            <option value="fee:desc">Fee: high → low</option>
            <option value="fee:asc">Fee: low → high</option>
            <option value="provider:asc">Provider A–Z</option>
            <option value="status:asc">Status A–Z</option>
          </select>
        </div>
        <div className="mt-3">
          <PeriodFilter dateFrom={dateFrom} dateTo={dateTo} onChange={(f, t) => { setDateFrom(f); setDateTo(t); setPage(1); }} testId="tx-period" />
        </div>
      </div>

      {/* Mobile cards */}
      <div className="space-y-3 md:hidden" data-testid="tx-cards">
        {isLoading ? (
          [...Array(4)].map((_, i) => <div key={i} className="h-28 animate-pulse rounded-md border border-border bg-[#E6EFF2]" />)
        ) : rows.length === 0 ? (
          <div className="rounded-md border border-border bg-card px-4 py-10 text-center text-muted-foreground" data-testid="tx-cards-empty">
            No transactions found. Import a transactions export (Import Data → Transactions).
          </div>
        ) : (
          rows.map((t, i) => (
            <div key={t.id} className="rounded-md border border-border bg-card p-4" data-testid={`tx-card-${i}`} onClick={() => openCustomer(t)}>
              <div className="mb-3 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="truncate font-medium">{t.customer_name || t.account_id || "—"}</div>
                  <div className="truncate font-mono text-xs text-muted-foreground">{t.tx_id}</div>
                </div>
                <StatusBadge status={t.status} />
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                <div><div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Amount</div><div className="font-mono text-sm">{fmtAmt(t.amount, t.currency)}</div></div>
                <div><div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Fee</div><div className="font-mono text-sm">{fmtAmt(t.fee_amount, t.currency)}</div></div>
                <div><div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Provider</div><div className="truncate font-mono text-xs">{t.provider || "—"}</div></div>
                <div><div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Date</div><div className="text-sm text-muted-foreground">{fmtDate(t.created_at)}</div></div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Desktop table */}
      <div className="hidden overflow-x-auto rounded-md border border-border bg-card md:block">
        <table className="w-full text-sm" data-testid="tx-table">
          <thead>
            <tr className="border-b border-border bg-[#EDF3F5] text-left">
              {["Date", "Transaction ID", "Customer", "Provider", "Direction", "Amount", "Fee", "Total", "Status"].map((h, i) => (
                <th key={h} className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground ${i >= 5 && i <= 7 ? "text-right" : ""}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              [...Array(6)].map((_, i) => (
                <tr key={i} className="border-b border-border"><td colSpan={9} className="px-4 py-3"><div className="h-4 w-full animate-pulse rounded bg-[#E6EFF2]" /></td></tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-4 py-10 text-center text-muted-foreground" data-testid="tx-table-empty">
                  <Receipt className="mx-auto mb-2 h-8 w-8 text-muted-foreground/50" />
                  No transactions found. Import a transactions export (Import Data → Transactions).
                </td>
              </tr>
            ) : (
              rows.map((t, i) => (
                <tr key={t.id} className="cursor-pointer border-b border-border transition-colors duration-150 hover:bg-[#E6EFF2]" data-testid={`tx-row-${i}`} onClick={() => openCustomer(t)}>
                  <td className="px-4 py-3 text-muted-foreground">{fmtDate(t.created_at)}</td>
                  <td className="max-w-[180px] truncate px-4 py-3 font-mono text-xs text-muted-foreground">{t.tx_id}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium">{t.customer_name || "—"}</div>
                    <div className="font-mono text-xs text-muted-foreground">{t.account_id || ""}</div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{t.provider || "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground">{t.direction || "—"}</td>
                  <td className="px-4 py-3 text-right font-mono tabular">{fmtAmt(t.amount, t.currency)}</td>
                  <td className="px-4 py-3 text-right font-mono tabular">{fmtAmt(t.fee_amount, t.currency)}</td>
                  <td className="px-4 py-3 text-right font-mono tabular">{fmtAmt(t.total_amount, t.currency)}</td>
                  <td className="px-4 py-3"><StatusBadge status={t.status} /></td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">Page {page} of {totalPages}</span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="tx-prev-page">
            <ChevronLeft className="h-4 w-4" /> Prev
          </Button>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="tx-next-page">
            Next <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
